import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Heading2, List, ListOrdered, Eraser } from "lucide-react";

const ALLOWED = { ALLOWED_TAGS: ["p", "br", "b", "strong", "i", "em", "u", "h2", "h3", "ul", "ol", "li", "div", "span"], ALLOWED_ATTR: [] };
const isHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s || "");

export const stripHtml = (s) => {
  if (!isHtml(s)) return s || "";
  const d = document.createElement("div");
  d.innerHTML = DOMPurify.sanitize(s.replace(/<(br|\/p|\/li|\/h[23]|\/div)>/gi, " $&"), ALLOWED);
  return (d.textContent || "").replace(/\s+/g, " ").trim();
};

export const RichText = ({ html, className = "", testid }) => (
  isHtml(html)
    ? <div className={`rich-text ${className}`} data-testid={testid} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, ALLOWED) }} />
    : <div className={`whitespace-pre-line ${className}`} data-testid={testid}>{html}</div>
);

const TOOLS = [
  [Bold, "bold"], [Italic, "italic"], [Heading2, "formatBlock", "h3"],
  [List, "insertUnorderedList"], [ListOrdered, "insertOrderedList"], [Eraser, "removeFormat"],
];

export const RichTextEditor = ({ value, onChange, testid, tall }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = isHtml(value) ? DOMPurify.sanitize(value, ALLOWED) : (value || "").replace(/\n/g, "<br>");
    }
  }, [value]);
  const run = (cmd, arg) => {
    ref.current.focus();
    if (cmd === "formatBlock") {
      const inHeading = document.queryCommandValue("formatBlock").toLowerCase() === arg;
      document.execCommand("formatBlock", false, inHeading ? "p" : arg);
    } else if (cmd === "removeFormat") {
      document.execCommand("removeFormat");
      document.execCommand("formatBlock", false, "p");
    } else document.execCommand(cmd, false, arg);
    onChange(ref.current.innerHTML);
  };
  return (
    <div className="rounded-md border border-input mt-1.5 focus-within:ring-1 focus-within:ring-ring" data-testid={testid}>
      <div className="flex gap-1 border-b border-input p-1">
        {TOOLS.map(([Icon, cmd, arg]) => (
          <button key={cmd + (arg || "")} type="button" onMouseDown={(e) => { e.preventDefault(); run(cmd, arg); }}
            className="h-8 w-8 rounded hover:bg-accent flex items-center justify-center" data-testid={`${testid}-${cmd}${arg ? `-${arg}` : ""}`} title={cmd}>
            <Icon className="h-4 w-4" />
          </button>
        ))}
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className={`rich-text ${tall ? "min-h-[200px] max-h-[60vh] resize-y" : "min-h-[120px] max-h-80"} overflow-auto px-3 py-2 text-sm outline-none`} data-testid={`${testid}-area`} />
    </div>
  );
};
